/**
 * CreateScheduleDialog Component
 * Dialog for creating a new schedule with type selection and name.
 */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  ScheduleType,
  ScheduleCreate,
  RoutineVisualization,
  DEFAULT_SCHEDULE_ICON,
  DEFAULT_SETTINGS,
  SCHEDULE_VALIDATION,
} from '../../types/schedule';
import { scheduleService } from '../../services/scheduleService';
import { useDevice } from '../../contexts/DeviceContext';

interface CreateScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (scheduleId: string) => void;
}

interface ScheduleTypeOption {
  type: ScheduleType;
  emoji: string;
  description: string;
}

const SCHEDULE_TYPES: ScheduleTypeOption[] = [
  {
    type: 'routine',
    emoji: '📋',
    description: 'Step-by-step sequence of activities',
  },
  {
    type: 'daily_schedule',
    emoji: '📅',
    description: 'Full day timeline with timed activities',
  },
  {
    type: 'weekly_schedule',
    emoji: '📆',
    description: 'Plan activities for each day of the week',
  },
  {
    type: 'first_then',
    emoji: '➡️',
    description: 'Two-panel motivational board',
  },
  {
    type: 'choice_board',
    emoji: '🔲',
    description: 'Grid of options to choose from',
  },
  {
    type: 'waiting_board',
    emoji: '⏳',
    description: 'Timer with activity suggestions',
  },
];

export const CreateScheduleDialog: React.FC<CreateScheduleDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { selectedDevice } = useDevice();
  const [selectedType, setSelectedType] = useState<ScheduleType>('routine');
  const [name, setName] = useState('');
  const [routineVisualization, setRoutineVisualization] = useState<RoutineVisualization>('overview_then_fullscreen');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!saving) {
      setSelectedType('routine');
      setName('');
      setRoutineVisualization('overview_then_fullscreen');
      setError(null);
      onClose();
    }
  };

  const handleCreate = async () => {
    if (!selectedDevice || !name.trim()) return;

    // Validation
    if (name.length > SCHEDULE_VALIDATION.NAME_MAX_LENGTH) {
      setError(t('schedules.errors.nameTooLong', { max: SCHEDULE_VALIDATION.NAME_MAX_LENGTH }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const scheduleData: ScheduleCreate = {
        type: selectedType,
        name: name.trim(),
        icon: DEFAULT_SCHEDULE_ICON[selectedType],
        // Include visualization mode for routines
        settings: selectedType === 'routine' ? {
          ...DEFAULT_SETTINGS,
          routineVisualization,
        } : undefined,
      };

      const created = await scheduleService.createSchedule(
        selectedDevice.id,
        scheduleData
      );

      handleClose();
      onCreated(created.id);
    } catch (err) {
      console.error('Failed to create schedule:', err);
      setError(t('schedules.errors.failedToCreateSchedule'));
    } finally {
      setSaving(false);
    }
  };

  const isValid = name.trim().length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('schedules.createNew')}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Schedule Type Selection */}
        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
          <InputLabel>{t('schedules.fields.type')}</InputLabel>
          <Select
            value={selectedType}
            label={t('schedules.fields.type')}
            onChange={(e) => setSelectedType(e.target.value as ScheduleType)}
            disabled={saving}
            renderValue={(value) => {
              const option = SCHEDULE_TYPES.find((opt) => opt.type === value);
              return option ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{option.emoji}</span>
                  <span>{t(`schedules.types.${option.type}`)}</span>
                </Box>
              ) : null;
            }}
          >
            {SCHEDULE_TYPES.map((option) => (
              <MenuItem key={option.type} value={option.type}>
                <ListItemIcon sx={{ minWidth: 36, fontSize: '1.25rem' }}>
                  {option.emoji}
                </ListItemIcon>
                <ListItemText primary={t(`schedules.types.${option.type}`)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Layout Preview for board types */}
        {(selectedType === 'first_then' || selectedType === 'choice_board') && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              bgcolor: alpha(
                selectedType === 'first_then' ? '#2196f3' : '#9c27b0',
                0.05
              ),
              border: 1,
              borderColor: selectedType === 'first_then' ? '#2196f3' : '#9c27b0',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              {t('schedules.createPreview.title')}
            </Typography>

            {selectedType === 'first_then' ? (
              // First-Then preview
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: alpha('#2196f3', 0.2),
                    borderRadius: 1,
                    p: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="#1565c0">
                    {t('schedules.firstThen.first')}
                  </Typography>
                  <Box sx={{ fontSize: '1.5rem', mt: 0.5 }}>📝</Box>
                </Box>
                <ArrowIcon sx={{ color: 'grey.400' }} />
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: alpha('#4caf50', 0.2),
                    borderRadius: 1,
                    p: 1.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={600} color="#2e7d32">
                    {t('schedules.firstThen.then')}
                  </Typography>
                  <Box sx={{ fontSize: '1.5rem', mt: 0.5 }}>🎁</Box>
                </Box>
              </Box>
            ) : (
              // Choice Board preview: 3 slots in a row, as the tablet shows them in landscape
              <>
                <Box
                  data-testid="choice-board-preview"
                  aria-hidden
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 1,
                  }}
                >
                  {['🎮', '📚', '🎨'].map((emoji, i) => (
                    <Box
                      key={i}
                      data-testid="choice-board-preview-slot"
                      sx={{
                        bgcolor: alpha('#9c27b0', 0.15),
                        borderRadius: 1,
                        p: 1,
                        textAlign: 'center',
                        fontSize: '1.2rem',
                      }}
                    >
                      {emoji}
                    </Box>
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {t('schedules.choiceBoard.limitHelp')}
                </Typography>
              </>
            )}
          </Paper>
        )}

        {/* Name field */}
        <TextField
          autoFocus
          fullWidth
          label={t('schedules.fields.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: SCHEDULE_VALIDATION.NAME_MAX_LENGTH }}
          error={name.length > SCHEDULE_VALIDATION.NAME_MAX_LENGTH}
          helperText={`${name.length}/${SCHEDULE_VALIDATION.NAME_MAX_LENGTH}`}
          disabled={saving}
        />

        {/* Visualization Mode for routines */}
        {selectedType === 'routine' && (
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>{t('schedules.routineVisualization.title')}</InputLabel>
            <Select
              value={routineVisualization}
              label={t('schedules.routineVisualization.title')}
              onChange={(e) => setRoutineVisualization(e.target.value as RoutineVisualization)}
              disabled={saving}
            >
              <MenuItem value="overview_only">
                {t('schedules.routineVisualization.overview_only')}
              </MenuItem>
              <MenuItem value="overview_then_fullscreen">
                {t('schedules.routineVisualization.overview_then_fullscreen')}
              </MenuItem>
              <MenuItem value="fullscreen_only">
                {t('schedules.routineVisualization.fullscreen_only')}
              </MenuItem>
            </Select>
          </FormControl>
        )}


      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!isValid || saving}
          startIcon={saving && <CircularProgress size={16} />}
        >
          {saving ? t('common.saving') : t('common.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
